import type { components } from "./api-schema";

export type ProtocolIssue = components["schemas"]["ProtocolIssue"];
export type ProtocolGroup = {
  key: string;
  product: string;
  mark: string;
  building: string;
  discipline: string;
  category: string;
  general: boolean;
  issues: ProtocolIssue[];
  works: { id: string | null; name: string; issues: ProtocolIssue[] }[];
};

// Display vocabulary only: a category/mark is not a material identity in this API.
const PRODUCT_NAMES = new Map([
  ["Двери", "Дверь"],
  ["Окна", "Окно"],
  ["Полы", "Пол"],
  ["Щиты", "Щит"],
  ["Светильники", "Светильник"],
]);

/** Group the complete filtered list before pagination, preserving every original issue. */
export function groupProtocolIssues(issues: readonly ProtocolIssue[], revisionId: string): ProtocolGroup[] {
  const groups = new Map<string, ProtocolGroup>();
  const works = new Map<string, Map<string | null, ProtocolGroup["works"][number]>>();
  for (const issue of issues) {
    const general = issue.work_id == null;
    // Do not normalize marks: suffixes, spelling and case may distinguish types.
    const mark = !general && issue.group.trim() ? issue.group : "";
    const context = [revisionId, issue.building, issue.discipline, issue.category];
    const key = JSON.stringify(general
      ? [...context, issue.document_id ? "document" : "issue", issue.document_id || issue.id]
      : [...context, mark ? "mark" : "work", mark || issue.work_id]);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        product: general
          ? issue.document_id || issue.kind === "unread_document" ? "Документ" : "Общее замечание"
          : PRODUCT_NAMES.get(issue.category) || issue.category || "Позиция",
        mark,
        building: issue.building,
        discipline: issue.discipline,
        category: issue.category,
        general,
        issues: [],
        works: [],
      };
      groups.set(key, group);
      works.set(key, new Map());
    }
    group.issues.push(issue);
    const groupWorks = works.get(key)!;
    let work = groupWorks.get(issue.work_id);
    if (!work) {
      work = { id: issue.work_id, name: issue.work, issues: [] };
      groupWorks.set(issue.work_id, work);
      group.works.push(work);
    }
    work.issues.push(issue);
  }
  return [...groups.values()];
}

/** Evidence limitations, not an operator-verification or decision status. */
export function protocolAttention(issue: ProtocolIssue): string | null {
  const labels: string[] = [];
  switch (issue.kind) {
    case "incomplete": labels.push("Недостаточно данных"); break;
    case "units": labels.push("Единицы нельзя сопоставить автоматически"); break;
    case "recognition": labels.push("Проверка распознавания"); break;
    case "unread_document": labels.push("Документ не распознан"); break;
  }
  const addressStates = [issue.left?.address_state, issue.right?.address_state];
  if (addressStates.includes("unresolved")) labels.push("Адрес источника не установлен");
  else if (addressStates.includes("approximate")) labels.push("Приблизительный адрес источника");
  // Protocol construction strips measurement.verification. Its schema default is
  // not proof of an operator check; decisions likewise do not change source facts.
  return labels.length ? labels.join(" · ") : null;
}
